import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook to determine user's subscription tier and feature access.
 * Returns: { tier, isPremium, plan, canAccess(feature) }
 */
export const useSubscription = () => {
  const { user } = useAuth();

  // `tier` here is the LEGACY two-bucket signal (free | premium) used
  // by the old FEATURE_ACCESS map below. Keep it for backward compat.
  const tier = useMemo(() => {
    if (!user) return 'free';

    // Pioneer (Founding Member) → unlimited, lifetime. Admins get the
    // same effective access. Without this, users like
    // printassistpro@gmail.com (who only have founder_pioneer=true and
    // no subscription_plan field) were defaulting to 'free' and being
    // offered "Upgrade to Scholar" CTAs on /pricing. (iter191 fix.)
    if (user.founder_pioneer || user.is_admin) return 'premium';

    // Ambassador access
    if (user.ambassador_claimed && user.ambassador_access_until) {
      const expiry = new Date(user.ambassador_access_until);
      if (expiry > new Date()) return 'premium';
    }

    // Active paid subscription
    if (
      user.subscription_status === 'active' &&
      ['pro', 'premium', 'student', 'scholar'].includes(user.subscription_plan)
    ) {
      return 'premium';
    }

    // Premium until date
    if (user.premium_until) {
      const until = new Date(user.premium_until);
      if (until > new Date()) return 'premium';
    }

    return 'free';
  }, [user]);

  const isPremium = tier === 'premium';

  const plan = useMemo(() => {
    if (!user) return null;
    // Pioneer/Admin → top-of-stack 'pro' plan id (matches TIER_ORDER in
    // /lib/tiers.js). PricingPage uses `plan === tier.id` to render the
    // "Current Plan" badge, so they MUST land on a real tier id.
    if (user.founder_pioneer || user.is_admin) return 'pro';
    return user.subscription_plan || (isPremium ? 'ambassador' : 'free');
  }, [user, isPremium]);

  // Feature access map: true = all tiers, 'premium' = premium only
  const FEATURE_ACCESS = {
    // Free features
    'lectures': true,
    'transcription-basic': true,
    'flashcards': true,
    'study-buddy-basic': true,
    'study-groups': true,
    'subjects': true,
    'schedule': true,
    'achievements': true,
    'referrals': true,
    // Premium features
    'ai-exam-prep': 'premium',
    'advanced-analytics': 'premium',
    'unlimited-transcription': 'premium',
    'ai-tutor-premium': 'premium',
    'document-analysis': 'premium',
    'campus-plans': 'premium',
    'api-access': 'premium',
    'priority-support': 'premium',
    'custom-ai-prompts': 'premium',
    'multi-speaker': 'premium',
  };

  const canAccess = (feature) => {
    const access = FEATURE_ACCESS[feature];
    if (access === true) return true;
    if (access === 'premium') return isPremium;
    return true; // unknown features default to accessible
  };

  return { tier, isPremium, plan, canAccess };
};
